export type RtoSlab = {
    fuelType?: string;
    ownershipType?: string;
    engineCcFrom?: number | null;
    engineCcTo?: number | null;
    priceFrom?: number | null; // Added for states like KA/TS
    priceTo?: number | null;
    rtoPercent: number;
    fixedAmount?: number;      // Registration + Smart Card
    surchargePercent?: number; // Rajasthan/Gujarat style (applied on RTO amount)
    urbanAssessmentTaxPercent?: number;
    infraCessPercent?: number; // Goa/Kerala style (applied on Base value)
    socialSecurityCessAmount?: number;
    tempRegistrationAmount?: number;
    hypothecationFee?: number;
    hsrpCharges?: number;
    smartCardFee?: number;
    roadSafetyTaxAmount?: number;
    greenTaxAmount?: number;
    mcdParkingFee?: number;    // Delhi style
    _id?: string;
};

export type StateRtoConfig = {
    stateCode: string;
    rtoSlabs: RtoSlab[];
    secondVehicleTaxPercent?: number;
};

export type CalcRoadTaxInput = {
    base: number; // Ex-showroom price
    engineCc?: number;
    fuelType?: string;
    ownershipType?: string;
    stateConfig: StateRtoConfig;
    isSecondVehicle?: boolean;
};

export type CalcRoadTaxResult = {
    total: number;
    breakdown: {
        base: number;
        rtoAmount: number;
        surchargeAmount: number;
        infraCessAmount: number;
        greenTax: number;
        parkingFee: number;
        fixedFees: number;
        secondVehicleTax?: number;
    };
    // slab: RtoSlab | null;
};

/**
 * States that have a dedicated CNG slab in the DB — CNG must NOT be normalised to PETROL.
 * maharashtra: hiked CNG rate (8/9%), uttar-pradesh: lower CNG rate (7%), gujarat: flat slab that includes CNG.
 */
const STATES_WITH_DEDICATED_CNG_SLAB = new Set(['maharashtra', 'uttar-pradesh', 'gujarat']);

/**
 * States where CNG shares the PETROL slab (no dedicated CNG entry in the DB).
 * For these, try an exact CNG match first; if nothing is found fall back to the PETROL slab.
 */
const STATES_WITH_CNG_PETROL_FALLBACK = new Set(['delhi']);

/**
 * Enhanced matching logic for 2026 state rules including Engine CC and Price brackets.
 *
 * @param strictFuelMatch – when true the fuel type is matched exactly (no CNG→PETROL normalisation).
 */
function matchesSlab(
    slab: RtoSlab,
    fuelType?: string,
    ownershipType?: string,
    engineCc?: number,
    basePrice?: number,
    strictFuelMatch = false,
) {
    const normalizeFuelType = (fuel?: string) => {
        if (!fuel) return '';
        const normalized = fuel.toUpperCase();
        // Only collapse CNG→PETROL when we are NOT doing a strict match
        return (!strictFuelMatch && normalized === 'CNG') ? 'PETROL' : normalized;
    };

    const normalizedSlabFuel = normalizeFuelType(slab.fuelType);
    const normalizedInputFuel = normalizeFuelType(fuelType);

    if (normalizedSlabFuel && normalizedInputFuel && normalizedSlabFuel !== normalizedInputFuel) return false;
    if (slab.ownershipType && ownershipType && slab.ownershipType.toUpperCase() !== ownershipType.toUpperCase()) return false;

    // Engine CC Range Check
    // API stores 0 to mean "no upper limit" — must treat 0 the same as null/undefined
    const ccFrom = slab.engineCcFrom ?? 0;
    const ccTo = slab.engineCcTo ? slab.engineCcTo : Number.MAX_SAFE_INTEGER;
    if (engineCc !== undefined && (engineCc < ccFrom || engineCc > ccTo)) return false;

    // Price Range Check (Crucial for states like Karnataka/Delhi)
    // API stores 0 to mean "no upper limit" — must treat 0 the same as null/undefined
    const pFrom = slab.priceFrom ?? 0;
    const pTo = slab.priceTo ? slab.priceTo : Number.MAX_SAFE_INTEGER;
    if (basePrice !== undefined && (basePrice < pFrom || basePrice > pTo)) return false;

    return true;
}

/**
 * Standardized Road Tax Calculator 2026
 */
export function calculateRoadTaxBkup(input: CalcRoadTaxInput): CalcRoadTaxResult | null {
        
    const { base, engineCc, fuelType, ownershipType, stateConfig, isSecondVehicle } = input;
    const slabs = stateConfig.rtoSlabs || [];

    // Find matching slab - DO NOT fallback if no match found
    const slab = slabs.find(s => matchesSlab(s, fuelType, ownershipType, engineCc, base)) ?? null;

    // If no matching slab found, return null instead of using incorrect fallback
    if (!slab) {
        console.warn(`No matching RTO slab found for fuel type: ${fuelType}, engineCc: ${engineCc}`);
        return null;
    }

    // 1. Base RTO Calculation
    const rtoPercent = (slab?.rtoPercent ?? 0) / 100;
    const rtoAmount = base * rtoPercent;

    // 2. Surcharge (Calculation on Tax Amount - RJ Style)
    const surchargePercent = (slab?.surchargePercent ?? 0) / 100;
    const surchargeAmount = rtoAmount * surchargePercent;

    // 3. Infrastructure Cess (Calculation on Base Price - Goa/Kerala Style)
    const infraCessPercent = (slab?.infraCessPercent ?? 0) / 100;
    const infraCessAmount = base * infraCessPercent;

    // 4. Fixed Fees & Taxes
    const greenTax = slab?.greenTaxAmount ?? 0;
    const parkingFee = slab?.mcdParkingFee ?? 0;
    const fixedFees = (slab?.fixedAmount ?? 0) + (slab?.roadSafetyTaxAmount ?? 0);

    // 5. Second Vehicle Penalty
    let secondVehicleTax = 0;
    if (isSecondVehicle && stateConfig.secondVehicleTaxPercent) {
        secondVehicleTax = base * (stateConfig.secondVehicleTaxPercent / 100);
    }

    const total = Math.round(
        rtoAmount + surchargeAmount + infraCessAmount + greenTax + parkingFee + fixedFees + secondVehicleTax
    );

    return {
        total,
        breakdown: {
            base,
            rtoAmount: Math.round(rtoAmount),
            surchargeAmount: Math.round(surchargeAmount),
            infraCessAmount: Math.round(infraCessAmount),
            greenTax,
            parkingFee,
            fixedFees,
            secondVehicleTax: secondVehicleTax > 0 ? Math.round(secondVehicleTax) : undefined,
        },
        // slab,
    };
}

export function calculateRoadTax(input: CalcRoadTaxInput): CalcRoadTaxResult | null {
    const { base, engineCc, fuelType, ownershipType, stateConfig, isSecondVehicle } = input;

    const slabs = stateConfig.rtoSlabs || [];
    const stateCode = stateConfig.stateCode;   // slug e.g. "maharashtra", "delhi"
    const isCNG = fuelType?.toUpperCase() === 'CNG';

    // 1. Finding the correct slab
    // Strategy depends on whether the state has dedicated CNG slabs in the DB:
    //   • STATES_WITH_DEDICATED_CNG_SLAB (MH, UP, GJ):
    //       Match CNG strictly — do NOT fall back to PETROL slab.
    //   • STATES_WITH_CNG_PETROL_FALLBACK (DL):
    //       Try exact CNG match first; if nothing found, fall back to PETROL slab.
    //   • All other states:
    //       Legacy behaviour — normalise CNG to PETROL before matching.
    let slab: RtoSlab | null = null;

    if (isCNG && STATES_WITH_DEDICATED_CNG_SLAB.has(stateCode)) {
        // Strict CNG match — never collapse to PETROL
        slab = slabs.find(s => matchesSlab(s, fuelType, ownershipType, engineCc, base, true)) ?? null;
    } else if (isCNG && STATES_WITH_CNG_PETROL_FALLBACK.has(stateCode)) {
        // Try CNG first, then fall back to PETROL if no dedicated slab exists
        slab = slabs.find(s => matchesSlab(s, fuelType, ownershipType, engineCc, base, true)) ?? null;
        if (!slab) {
            slab = slabs.find(s => matchesSlab(s, 'PETROL', ownershipType, engineCc, base, false)) ?? null;
        }
    } else {
        // Default: normalise CNG → PETROL for states without separate CNG slabs
        slab = slabs.find(s => matchesSlab(s, fuelType, ownershipType, engineCc, base, false)) ?? null;
    }

    if (!slab) return null;

    // 2. Base Calculation (Standard Road Tax)
    // Note: Company ownership in MH/DL is often 2x the Individual rate
    const multiplier = (ownershipType?.toUpperCase() === 'COMPANY') ? 2 : 1;
    const rtoAmount = (base * ((slab.rtoPercent ?? 0) / 100)) * multiplier;

    // 3. Surcharges on Tax Amount (RJ Style - 12.5% of RTO Tax)
    const surchargeAmount = rtoAmount * ((slab.surchargePercent ?? 0) / 100);
    const urbanTaxAmount = rtoAmount * ((slab.urbanAssessmentTaxPercent ?? 0) / 100);

    // 4. Cess on Base Price (KA/GA Style)
    const infraCessAmount = base * ((slab.infraCessPercent ?? 0) / 100);

    // 5. Mandatory Fixed Fees & Add-ons (2026 Standards)
    const fixedFees = 
        (slab.fixedAmount ?? 0) + 
        (slab.roadSafetyTaxAmount ?? 0) + 
        (slab.socialSecurityCessAmount ?? 0) + 
        (slab.tempRegistrationAmount ?? 0) + 
        (slab.hypothecationFee ?? 0) + 
        (slab.hsrpCharges ?? 0) + 
        (slab.smartCardFee ?? 0);

    // 6. Pollution & Urban Penalties
    const greenTax = slab.greenTaxAmount ?? 0;
    const parkingFee = slab.mcdParkingFee ?? 0; // Delhi Specific
    const secondVehicleTax = isSecondVehicle 
        ? base * ((stateConfig.secondVehicleTaxPercent ?? 0) / 100) 
        : 0;

    const total = Math.round(
        rtoAmount + surchargeAmount + urbanTaxAmount + infraCessAmount + 
        fixedFees + greenTax + parkingFee + secondVehicleTax
    );

    return {
        total,
        breakdown: {
            base,
            rtoAmount: Math.round(rtoAmount),
            surchargeAmount: Math.round(surchargeAmount),
            infraCessAmount: Math.round(infraCessAmount),
            greenTax,
            parkingFee,
            fixedFees,
            secondVehicleTax: secondVehicleTax || undefined
        }
    };
}


export default calculateRoadTax;
